import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate, type RouteObject } from "react-router";
import { RouterErrorElement } from "@/components/ErrorBoundary";
import { RouteFallback } from "@/components/RouteFallback";
import { ProjectLayout } from "@/components/layout/ProjectLayout";
import { RequireAuth, RequireGuest } from "@/components/RouteGuards";
import { SettingsLayout } from "@/components/layout/SettingsLayout";
import { DesignLayout } from "@/components/layout/DesignLayout";

const AccountPage = lazy(() => import("@/pages/AccountPage").then((m) => ({ default: m.AccountPage })));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage").then((m) => ({ default: m.AdminDashboardPage })));
const AdminSupportPage = lazy(() => import("@/pages/AdminSupportPage").then((m) => ({ default: m.AdminSupportPage })));
const AgentPairPage = lazy(() => import("@/pages/AgentPairPage").then((m) => ({ default: m.AgentPairPage })));
const AttacksPage = lazy(() => import("@/pages/AttacksPage").then((m) => ({ default: m.AttacksPage })));
const CartographyPage = lazy(() => import("@/pages/CartographyPage").then((m) => ({ default: m.CartographyPage })));
const CheckEmailPage = lazy(() => import("@/pages/CheckEmailPage").then((m) => ({ default: m.CheckEmailPage })));
const CompleteGoogleSignupPage = lazy(() => import("@/pages/CompleteGoogleSignupPage").then((m) => ({ default: m.CompleteGoogleSignupPage })));
const ConfigPage = lazy(() => import("@/pages/ConfigPage").then((m) => ({ default: m.ConfigPage })));
const CurrenciesPage = lazy(() => import("@/pages/CurrenciesPage").then((m) => ({ default: m.CurrenciesPage })));
const DashboardPage = lazy(() => import("@/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })));
const ItemDetailPage = lazy(() => import("@/pages/ItemDetailPage").then((m) => ({ default: m.ItemDetailPage })));
const ItemsPage = lazy(() => import("@/pages/ItemsPage").then((m) => ({ default: m.ItemsPage })));
const LoginPage = lazy(() => import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const NotificationsSettingsPage = lazy(() => import("@/pages/NotificationsSettingsPage").then((m) => ({ default: m.NotificationsSettingsPage })));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })));
const PlacesPage = lazy(() => import("@/pages/PlacesPage").then((m) => ({ default: m.PlacesPage })));
const PluginPairPage = lazy(() => import("@/pages/PluginPairPage").then((m) => ({ default: m.PluginPairPage })));
const PricingPage = lazy(() => import("@/pages/PricingPage").then((m) => ({ default: m.PricingPage })));
const ProjectOverviewPage = lazy(() => import("@/pages/ProjectOverviewPage").then((m) => ({ default: m.ProjectOverviewPage })));
const QuestsPage = lazy(() => import("@/pages/QuestsPage").then((m) => ({ default: m.QuestsPage })));
const RecipesPage = lazy(() => import("@/pages/RecipesPage").then((m) => ({ default: m.RecipesPage })));
const RegisterPage = lazy(() => import("@/pages/RegisterPage").then((m) => ({ default: m.RegisterPage })));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })));
const StatsPage = lazy(() => import("@/pages/StatsPage").then((m) => ({ default: m.StatsPage })));
const SupportPage = lazy(() => import("@/pages/SupportPage").then((m) => ({ default: m.SupportPage })));
const UILabelsPage = lazy(() => import("@/pages/UILabelsPage").then((m) => ({ default: m.UILabelsPage })));
const UiTreesPage = lazy(() => import("@/pages/UiTreesPage").then((m) => ({ default: m.UiTreesPage })));
const ValidationReportPage = lazy(() => import("@/pages/ValidationReportPage"));
const Verify2FAPage = lazy(() => import("@/pages/Verify2FAPage").then((m) => ({ default: m.Verify2FAPage })));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage").then((m) => ({ default: m.VerifyEmailPage })));
const ThemeRoute = lazy(() =>
	import("@/pages/ThemePage").then((m) => ({
		default: function ThemeRouteComponent() {
			return (
				<m.ThemePremiumGate>
					<m.ThemePage />
				</m.ThemePremiumGate>
			);
		},
	})),
);

function lazyElement(element: ReactNode) {
	return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

function withErrorElement(routes: RouteObject[]): RouteObject[] {
	return routes.map((route) => {
		if (route.index) {
			return {
				...route,
				errorElement: route.errorElement ?? <RouterErrorElement />,
			};
		}
		return {
			...route,
			errorElement: route.errorElement ?? <RouterErrorElement />,
			children: route.children ? withErrorElement(route.children) : undefined,
		};
	});
}

export function createRouter(basename: string) {
	const routes = withErrorElement([
	{ path: "/", element: <Navigate to="/dashboard" replace /> },
	{
		path: "/login",
		element: (
			<RequireGuest>
				{lazyElement(<LoginPage />)}
			</RequireGuest>
		),
	},
	{
		path: "/register",
		element: (
			<RequireGuest>
				{lazyElement(<RegisterPage />)}
			</RequireGuest>
		),
	},
	{
		path: "/check-email",
		element: (
			<RequireGuest>
				{lazyElement(<CheckEmailPage />)}
			</RequireGuest>
		),
	},
	{
		path: "/complete-google-signup",
		element: (
			<RequireGuest>
				{lazyElement(<CompleteGoogleSignupPage />)}
			</RequireGuest>
		),
	},
	{
		path: "/verify-email",
		element: (
			<RequireGuest>
				{lazyElement(<VerifyEmailPage />)}
			</RequireGuest>
		),
	},
	{
		path: "/verify-2fa",
		element: (
			<RequireGuest>
				{lazyElement(<Verify2FAPage />)}
			</RequireGuest>
		),
	},
	{
		path: "/forgot-password",
		element: (
			<RequireGuest>
				{lazyElement(<ForgotPasswordPage />)}
			</RequireGuest>
		),
	},
	{
		path: "/reset-password",
		element: (
			<RequireGuest>
				{lazyElement(<ResetPasswordPage />)}
			</RequireGuest>
		),
	},
	{
		path: "/dashboard",
		element: (
			<RequireAuth>
				{lazyElement(<DashboardPage />)}
			</RequireAuth>
		),
	},
	{
		path: "/admin-dashboard",
		element: (
			<RequireAuth>
				{lazyElement(<AdminDashboardPage />)}
			</RequireAuth>
		),
	},
	{
		path: "/support",
		element: (
			<RequireAuth>
				{lazyElement(<SupportPage />)}
			</RequireAuth>
		),
	},
	{
		path: "/support/:id",
		element: (
			<RequireAuth>
				{lazyElement(<SupportPage />)}
			</RequireAuth>
		),
	},
	{
		path: "/admin-support",
		element: (
			<RequireAuth>
				{lazyElement(<AdminSupportPage />)}
			</RequireAuth>
		),
	},
	{
		path: "/admin-support/:id",
		element: (
			<RequireAuth>
				{lazyElement(<AdminSupportPage />)}
			</RequireAuth>
		),
	},
	{
		path: "/account",
		element: (
			<RequireAuth>
				{lazyElement(<AccountPage />)}
			</RequireAuth>
		),
	},
	{
		path: "/pricing",
		element: (
			<RequireAuth>
				{lazyElement(<PricingPage />)}
			</RequireAuth>
		),
	},
	{
		path: "/plugin/pair",
		element: (
			<RequireAuth>
				{lazyElement(<PluginPairPage />)}
			</RequireAuth>
		),
	},
	{
		path: "/agent/pair",
		element: (
			<RequireAuth>
				{lazyElement(<AgentPairPage />)}
			</RequireAuth>
		),
	},
	{
		path: "/projects/:slug",
		element: (
			<RequireAuth>
				<ProjectLayout />
			</RequireAuth>
		),
		children: [
			{ index: true, element: <Navigate to="items" replace /> },
			{ path: "items", element: lazyElement(<ItemsPage />) },
			{ path: "items/new", element: lazyElement(<ItemDetailPage />) },
			{ path: "items/new/:template", element: lazyElement(<ItemDetailPage />) },
			{ path: "items/:id", element: lazyElement(<ItemDetailPage />) },
			{ path: "attacks", element: lazyElement(<AttacksPage />) },
			{ path: "attacks/new", element: lazyElement(<AttacksPage />) },
			{ path: "attacks/:id", element: lazyElement(<AttacksPage />) },
			{ path: "recipes", element: lazyElement(<RecipesPage />) },
			{ path: "recipes/new", element: lazyElement(<RecipesPage />) },
			{ path: "recipes/:id", element: lazyElement(<RecipesPage />) },
			{ path: "currencies", element: lazyElement(<CurrenciesPage />) },
			{ path: "currencies/new", element: lazyElement(<CurrenciesPage />) },
			{ path: "currencies/:id", element: lazyElement(<CurrenciesPage />) },
			{ path: "stats", element: lazyElement(<StatsPage />) },
			{ path: "stats/new", element: lazyElement(<StatsPage />) },
			{ path: "stats/:id", element: lazyElement(<StatsPage />) },
			{ path: "quests", element: lazyElement(<QuestsPage />) },
			{ path: "quests/new", element: lazyElement(<QuestsPage />) },
			{ path: "quests/:id", element: lazyElement(<QuestsPage />) },
			{ path: "places", element: lazyElement(<PlacesPage />) },
			{ path: "places/new", element: lazyElement(<PlacesPage />) },
			{ path: "places/:id", element: lazyElement(<PlacesPage />) },
			{
				path: "design",
				element: <DesignLayout />,
				children: [
					{ index: true, element: <Navigate to="theme" replace /> },
					{ path: "theme", element: lazyElement(<ThemeRoute />) },
					{ path: "ui-trees", element: lazyElement(<UiTreesPage />) },
					{ path: "ui-trees/:id", element: lazyElement(<UiTreesPage />) },
				],
			},
			{ path: "validation", element: lazyElement(<ValidationReportPage />) },
			{
				path: "settings",
				element: <SettingsLayout />,
				children: [
					{ index: true, element: <Navigate to="project" replace /> },
					{ path: "project", element: lazyElement(<ProjectOverviewPage />) },
					{ path: "config", element: lazyElement(<ConfigPage />) },
					{ path: "ui-labels", element: lazyElement(<UILabelsPage />) },
					{ path: "notifications", element: lazyElement(<NotificationsSettingsPage />) },
					{ path: "cartography", element: lazyElement(<CartographyPage />) },
				],
			},
		],
	},
	{ path: "*", element: lazyElement(<NotFoundPage />) },
	]);
	return createBrowserRouter(routes, { basename });
}
